import qs from 'qs';
import { get, internalDestroy, internalGet, patch, post, put } from './';
import { API_NODE_BASE } from '../../constants';

// getHotels keeps serving the admin-catalog listings (POST /api/v2/hotels).
// Availability search now uses the internal BFF endpoints below.
export const getHotels = ({ filters, locale, skip }) => {
  if (skip) {
    return Promise.resolve(null);
  }

  return post(`/hotels?locale=${locale ?? 'en'}`, filters, {
    baseURL: `${API_NODE_BASE}/api/v2`,
  });
};

const sanitizeAdults = (adults) => {
  const parsed = Math.trunc(Number(adults));
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

const sanitizeRoom = (room) => ({
  adults: sanitizeAdults(room?.adults),
  ...(room?.children !== undefined && { children: room.children }),
  childrenBirthdates: room?.childrenBirthdates ?? [],
  firstAdultNationality:
    typeof room?.firstAdultNationality === 'string'
      ? room.firstAdultNationality
      : (room?.firstAdultNationality?.value ?? ''),
});

// Each composition entry is one physical room. Drop the legacy `count` field
// (it holds pax, not a room multiplier) and sanitize 1:1.
const formatRooms = (rooms = []) =>
  // eslint-disable-next-line no-unused-vars
  (rooms ?? []).map(({ count, ...room }) => sanitizeRoom(room));

// Translate the FE filter object into the internal availability query:
// rename the stay window, expand rooms, drop server-derived fields.
const toAvailabilityQuery = ({
  startAt,
  endAt,
  rooms,
  bypassLocationRequirement,
  dmcId,
  includePartnerHotels,
  hasTemplatePrice,
  ...rest
} = {}) => ({
  ...rest,
  checkIn: startAt,
  checkOut: endAt,
  rooms: formatRooms(rooms),
});

// qs drops an empty `rooms` array entirely, so a caller that mounts before the
// user has picked occupancy would send a roomsless URL the BFF can only answer
// with a 400. Treat it as "not ready", same as `skip`.
const hasOccupancy = (filters) =>
  (filters?.rooms ?? []).some((room) => Number(room?.adults) > 0);

export const getHotelsAvailability = ({ filters, locale, skip }) => {
  if (skip || !hasOccupancy(filters)) {
    return Promise.resolve(null);
  }

  const query = qs.stringify(toAvailabilityQuery(filters), {
    arrayFormat: 'indices',
    skipNulls: true,
  });
  return get(
    `/api/internal/hotels/availability?locale=${locale ?? 'en'}&${query}`,
    {
      baseURL: API_NODE_BASE,
    },
  );
};

// The internal rooms DTO nests supplier:{name,code,flux:{name,code,logo}|null};
// downstream consumers (roomKeyGenerator, booking tunnel, checkout) read the flat
// supplier/supplierCode/source/sourceLogo, so flatten here at the one seam. The
// old top-level `source`/`sourceLogo` are now the supplier's `flux` — the Koedia
// trigramme that sold the rate (Expedia, Sunhotels, Shangri La, ...); null off
// Koedia. `?? flat` keeps it working against a not-yet-nested API response.
const flattenSupplierSource = (possibility) => ({
  ...possibility,
  supplier: possibility?.supplier?.name ?? possibility?.supplier,
  supplierCode: possibility?.supplier?.code ?? possibility?.supplierCode,
  source: possibility?.supplier?.flux?.name ?? possibility?.source,
  sourceLogo: possibility?.supplier?.flux?.logo ?? possibility?.sourceLogo,
  compatibleRoomCompositions: (
    possibility?.compatibleRoomCompositions ?? []
  ).map(flattenSupplierSource),
});

export const getRoomsAvailability = ({ id, filters, locale }) => {
  const query = qs.stringify(toAvailabilityQuery(filters), {
    arrayFormat: 'indices',
    skipNulls: true,
  });
  return get(
    `/api/internal/hotels/${id}/rooms/availability?locale=${locale ?? 'en'}&${query}`,
    {
      baseURL: API_NODE_BASE,
    },
  ).then((res) => {
    const env = res?.data ?? res;
    // Internal envelope: { requestId, expiresAt, data: [...possibilities] }.
    // Normalize to a single canonical shape for all consumers.
    return {
      requestId: env?.requestId,
      expiresAt: env?.expiresAt,
      possibilities: (env?.data ?? []).map(flattenSupplierSource),
    };
  });
};

export const getHotel = async ({ hotelId, skip, locale, tripId }) => {
  if (skip || !hotelId) {
    return Promise.resolve(null);
  }

  return get(
    `/hotels/${hotelId}?locale=${locale ?? 'en'}${
      tripId ? `&tripId=${tripId}` : ''
    }`,
  );
};

export const getHotelAllotments = async ({ hotelId, from, to }) => {
  return get(`/hotels/${hotelId}/allotments?from=${from}&to=${to}`);
};

export const updateHotelAllotments = async ({ hotelId, payload }) => {
  return patch(`/hotels/${hotelId}/allotments`, payload);
};
export const getHotelCancellationPolicies = async ({ id, locale }) => {
  return get(`/hotels/${id}/cancellationPolicies?locale=${locale ?? 'en'}`);
};

export const createHotel = async (payload) => {
  return post(`/hotels/create`, payload);
};

/**
 *
 * @param {string} hotelId
 * @param {UpdateTimezoneRequest} timezone
 */
export const updateHotelTimezone = async (hotelId, timezone) => {
  return patch(`/hotels/${hotelId}/timezone`, {
    timezone: timezone.timezone,
  });
};

export const submitBook = ({ id, data }) => {
  return post(`hotels/${id}/book`, data);
};

export const toggleIsChannelManagerConnected = async ({
  hotelId,
  connected,
}) => {
  return patch(`/hotels/${hotelId}/toggle-channel-manager`, {
    enable: connected,
  });
};

export const updateHotel = ({ hotelId, locale, payload }) => {
  return put(`/hotels/${hotelId}?locale=${locale ?? 'en'}`, payload);
};

export const archiveHotel = ({ hotelIds }) => {
  return internalDestroy(`/hotels`, hotelIds);
};

export const searchKoediaHotel = async ({ search }) => {
  if (!search || search.length < 3) {
    return Promise.resolve(null);
  }
  return internalGet(`/koedia/hotels?search=${search}`);
};

export const getKoediaHotelDetail = async ({
  supplierCode,
  accomCodes,
  skip,
}) => {
  if (skip) {
    return Promise.resolve(null);
  }
  return internalGet(
    `/koedia/hotels/${accomCodes}?supplierCode=${supplierCode}`,
  );
};

// KD-3815 — the organizations the current user may preview as (`viewAs`):
// its partner tour operators; any tour operator or DMC for a Koob admin.
export const getViewAsOptions = () =>
  get('/api/internal/hotels/availability/view-as', {
    baseURL: API_NODE_BASE,
  }).then((res) => res?.data?.data ?? []);

// KD-3815 — why a hotel is (or is not) bookable for `filters.viewAs` (the
// caller itself when absent): access rules, prices with their lines, each
// room's nights and reasons. Same filters as the availability search.
export const getHotelAvailabilityExplain = ({ id, filters, locale, skip }) => {
  if (skip) return Promise.resolve(null);
  const query = qs.stringify(toAvailabilityQuery(filters), {
    arrayFormat: 'indices',
    skipNulls: true,
  });
  return get(
    `/api/internal/hotels/${id}/availability/explain?locale=${locale ?? 'en'}&${query}`,
    { baseURL: API_NODE_BASE },
  ).then((res) => res?.data);
};
